import { adminRoles, mainIconSize } from '@/constants';
import { useDateFormatter, useRouteProtection } from '@/hooks';
import { useRoles } from '@/hooks/useRoles';
import type { OrgTableRow } from '@/types';
import { isForbiddenError } from '@/types/errors';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import ErrorMessage from '@components/ErrorMessage';
import { FullLoading } from '@components/Loaders';
import { Button, Tooltip } from '@nautilusbio/nb-design-system';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Table from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useLoaderData, useNavigate } from '@tanstack/react-router';
import type { ColDef } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { Plus, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import '../../styles/components/ag-grid-overrides.css';
import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';
import pageStyles from '../../styles/pages/index.module.css';

import { ActionsCellRenderer, OrgNameCell, SelectionCellRenderer, ContactCell } from './components';
import { useOrganizations } from './hooks/useOrganizations';

const tableContainerProps = {
  className: clsx(pageStyles.table, globalStyles.scrollbars),
};
const OrganizationsPage = () => {
  // Protect this route - will redirect if user loses SuperAdmin role or switches org
  useRouteProtection(adminRoles);

  const { t } = useTranslation('orgs');
  const { t: tGeneral } = useTranslation('general');
  const loaderOrgsRaw = useLoaderData({ from: '/organizations/' });
  const loaderOrgs = Array.isArray(loaderOrgsRaw) ? loaderOrgsRaw : [];

  const navigate = useNavigate();
  const formatDate = useDateFormatter();

  // Check if the current user is a global SuperAdmin (platform-level flag)
  const { isGlobalSuperAdmin } = useRoles();

  // Ref for table
  const gridRef = useRef<AgGridReact<OrgTableRow>>(null);

  // Use custom hook for organizations query
  const { data, isLoading, isPending, error, refetch: refetchOrganizations } = useOrganizations(loaderOrgs);

  const rowData: OrgTableRow[] = data ?? [];

  useEffect(() => {
    // Only 403 should land on /unauthorized. 401 means the session has expired
    // and ApiClient's global onUnauthorized handler is already navigating to
    // /login; racing it with a /unauthorized push strands the user there.
    if (error && isForbiddenError(error)) {
      void navigate({ to: '/unauthorized' });
    }
  }, [error, navigate]);

  // Column Definitions: Defines & controls grid columns.
  const colDefs = useMemo<ColDef<OrgTableRow>[]>(
    () => [
      {
        cellClass: pageStyles.selectedCell,
        cellRenderer: SelectionCellRenderer,
        field: 'id',
        filter: false,
        headerName: tGeneral('selected'),
        lockPosition: true,
        lockVisible: true,
        maxWidth: 20,
        resizable: false,
        sortable: false,
        suppressAutoSize: true,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
      },
      {
        field: 'name',
        cellRenderer: OrgNameCell,
      },
      {
        comparator(valueA, valueB) {
          const date1 = valueA && Date.parse(valueA);
          const date2 = valueB && Date.parse(valueB);

          if (Number.isNaN(date1) && Number.isNaN(date2)) return 0;
          if (Number.isNaN(date1)) return -1;
          if (Number.isNaN(date2)) return 1;

          return date1 - date2;
        },
        field: 'created_at',
        filter: 'agDateColumnFilter',
        headerName: tGeneral('created_at'),
        valueFormatter: ({ value }) => formatDate(value),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        field: 'num_studies',
        filter: 'agNumberColumnFilter',
        headerClass: tableStyles.tableCellCenter,
        headerName: tGeneral('studies'),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: ActionsCellRenderer,
        filter: false,
        headerName: tGeneral('actions'),
        maxWidth: 110,
        resizable: false,
        sortable: false,
        suppressAutoSize: true,
      },
      {
        field: 'alias',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('alias'),
        hide: true, // TODO: Update when backend returns alias data
      },
      {
        field: 'primary_contact',
        filter: false,
        headerName: tGeneral('primary_contact'),
        cellRenderer: ContactCell,
        hide: true, // TODO: Update when backend returns primary_contact data
      },
      {
        field: 'it_contact',
        filter: false,
        headerName: tGeneral('it_contact'),
        cellRenderer: ContactCell,
        hide: true, // TODO: Update when backend returns it_contact data
      },
    ],
    [tGeneral, formatDate],
  );

  // Promise-based refetch handler with toast feedback
  const handleRefetch = useCallback(async () => {
    const toastId = toast.loading(t('refetching'));
    try {
      await refetchOrganizations();
      toast.success(t('refetch_success'), { id: toastId });
    } catch {
      toast.error(t('list_refetch_error'), {
        id: toastId,
      });
    }
  }, [refetchOrganizations, t]);

  return (
    <Stack alignItems="start" className={pageStyles.organizationContainer} fullWidth>
      <Stack
        alignItems="center"
        className={clsx(pageStyles.titleRow, pageStyles.organizationTitle)}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Stack alignItems="center" fullHeight gap={2} justifyContent="center" orientation="horizontal">
          <Text as={'div'} className={getTextClassName('lg')}>
            {t('name')}
          </Text>

          <Tooltip
            content={t('refetch_tooltip')}
            placement="right-start"
            trigger={
              <Button data-testid={'refetch-orgs-btn'} onPress={handleRefetch} visual="unstyled">
                <RefreshCw size={16} />
              </Button>
            }
          />
        </Stack>

        {isGlobalSuperAdmin() && (
          <Button
            data-testid="create-org-btn"
            onPress={() => navigate({ to: '/organizations/new' })}
            size="md"
            visual="unstyled"
          >
            <Plus size={mainIconSize} />
            {t('create_org')}
          </Button>
        )}
      </Stack>

      {(isLoading || isPending) && <FullLoading label={t('fetching')} />}

      <div
        className={clsx(
          pageStyles.organizationList,
          globalStyles.scrollbars,
          (isLoading || isPending) && pageStyles.hide,
          error && pageStyles.errorContainer,
        )}
      >
        {error ? (
          <ErrorMessage error={error} level="error" />
        ) : (
          <Table
            aria-label={t('name')}
            columnDefs={colDefs}
            container={tableContainerProps}
            domLayout="autoHeight"
            enableResponsiveResize={false}
            resizeDebounceMs={150}
            ref={gridRef}
            rowData={rowData}
            tableId="organizations-table"
            onGridReady={e => sizeColumnsOnFirstVisit(e.api, 'organizations-table')}
            onGridPreDestroyed={e => flushColumnState(e.api, 'organizations-table')}
          />
        )}
      </div>
    </Stack>
  );
};

export default OrganizationsPage;
